import React, { FC } from 'react';
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useGetResourceEventsQuery } from '../healthWalletSlice';
import { formatDate } from '../healthWalletConstants';
import type { GroupedSummary } from '../healthWalletConstants';
import { EventTimeline } from '../EventTimeline';
import { DocHistoryError } from './DocHistoryError';
import { medStatusChip } from './classify';
import { V1_ACCENT } from './theme';

// ============================================================================
// Medications detail dialog (RendererV1) — same frame as ConditionDetailDialog
// (header w/ status chip, fact row, template prose, shared EventTimeline as the
// documentation history; period-bucketed timeline = phase 1b, same deferral).
// Adds the group-level dose/route meta the design's metaRows show; per-event
// details (incl. technical) already ride each timeline row from SQL.
// ============================================================================

export const MedicationDetailDialog: FC<{
  group: GroupedSummary | null;
  open: boolean;
  onClose: () => void;
}> = ({ group, open, onClose }) => {
  const { data: events, isLoading, isFetching, isError, refetch } = useGetResourceEventsQuery(
    { resource: 'medications', name: group?.name === 'Other / Uncategorized' ? '' : (group?.name ?? '') },
    { skip: !group || !open }
  );
  if (!group) return null;
  const chip = medStatusChip(group);
  const loading = isLoading || isFetching;
  const fact = (label: string, value: string, bordered = false) => (
    <Box sx={{ flex: 1, minWidth: 120, ...(bordered ? { borderLeft: '1px solid #f0f0f3', pl: 2 } : {}) }}>
      <Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 17, fontWeight: 700, mt: 0.5 }}>{value}</Typography>
    </Box>
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth transitionDuration={0}
      PaperProps={{ sx: { borderRadius: '22px', bgcolor: '#f7f5fb', overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', p: '24px 26px', borderBottom: '1px solid #ece9f4', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.15 }}>{group.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
            {chip ? (
              <Box sx={{ fontSize: '12.5px', fontWeight: 700, px: 1.5, py: 0.6, borderRadius: '20px', bgcolor: chip.bg, color: chip.fg }}>{chip.label}</Box>
            ) : null}
            <Typography sx={{ fontSize: '12.5px', color: '#8e8e93' }}>as documented in your health record</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ bgcolor: '#ececed', color: '#6b6b70' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ p: '22px !important' }}>
        {/* Fact row */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: 'white' }}>
          {/* Same calendar day for first & last → show one "Date recorded" (formatDate is day-level). */}
          {group.firstDate && formatDate(group.firstDate) === formatDate(group.lastDate) ? (
            fact('Date recorded', formatDate(group.firstDate))
          ) : (
            <>
              {fact('First documented', group.firstDate ? formatDate(group.firstDate) : '—')}
              {fact('Most recently documented', group.lastDate ? formatDate(group.lastDate) : '—', true)}
            </>
          )}
          {fact('Referenced in', `${group.count.toLocaleString('en-US')} records`, true)}
        </Paper>

        {/* Group-level medication meta (design metaRows) — "Not documented" per the design's own fallback. */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: 'white' }}>
          {fact('Dose', group.dose || 'Not documented')}
          {fact('Route', group.route || 'Not documented', true)}
          {fact('Indication', group.indication || 'Not documented', true)}
        </Paper>

        {/* About this record — template prose (NOT AI), mirrors the design's generated copy. */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, bgcolor: 'white' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#6b6b70', textTransform: 'uppercase', mb: 1 }}>About this record</Typography>
          <Typography sx={{ fontSize: '14.5px', lineHeight: 1.55, color: '#2a2a2c' }}>
            This medication has been documented across prescriptions, dispenses, and care records
            {group.firstDate ? `, first appearing in ${formatDate(group.firstDate)}` : ''}. This page summarizes how it appears
            in your health record; it does not provide medical or dosing advice.
          </Typography>
        </Paper>

        {/* Documentation history — shared EventTimeline (per-event expandable details incl. technical). */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px 8px', bgcolor: 'white' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#6b6b70', textTransform: 'uppercase', mb: 2 }}>Documentation history</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Box>
          ) : isError ? (
            <DocHistoryError onRetry={refetch} />
          ) : (
            <EventTimeline eventList={events ?? []} chartData={null} resource="medications" groupName={group.name} growWithContent />
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};
